import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Location, useLocation, useParams } from 'react-router';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import {
    IPaymentProvider,
    paymentProviderFactory,
    ThreeDSecureAuthenticationData
} from 'src/integrations/PaymentProvider/PaymentProvider';
import { addGuestOrder } from 'components/order/localGuestOrders';
import { OperationStatus } from 'components/order/model/Operation';
import {
    IOrderCreateResourceV8,
    OrderAdjustmentType,
    OrderErrorCodes,
    OrderPaymentType,
    OrderScenario
} from 'components/order/model/Order';
import { EOrderNonceKey, orderApi } from 'components/order/orderApi';
import { OPERATION_POLLING_DELAY } from 'config/constants';
import logger from 'lib/logger';
import { isDefined } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useCurrencyString } from 'lib/useCurrencyString';
import { useLocalHistory } from 'lib/useLocalHistory';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { partialResetBasket, resetBasket } from 'store/basket/basketSlice';
import { loadTimeslots } from 'store/settings/settingsSlice';
import { ApplicationState } from 'store/store';
import { userApi } from 'components/user/userApi';
import { getUserCard } from 'store/auth/authSlice';
import { useSnackbar } from 'notistack';
import { isStandAlonePaymentProvider } from 'src/utils/payment';

interface IOperationPageParams extends LocationRouteParams {
    operationId: string;
}

interface IOperationHistoryParams {
    orderBodyToRecreate?: IOrderCreateResourceV8;
    orderBodyToRecreatePat?: IOrderCreateResourceV8 & { id: number };
}

export enum OperationErrorType {
    GENERIC,
    NOT_FOUND,
    NONE,
    THREEDSECURE_AUTH_REQUIRED
}

const SPECIAL_ERROR_CODES: string[] = [
    OrderErrorCodes.DUPLICATE_PENDING_ORDER,
    OrderErrorCodes.UPDATING_THE_BILL
];

export function useOperationStatus() {
    const { operationId, locationId } = useParams<IOperationPageParams>();
    const { settings, timeslots } = useSelector((state: ApplicationState) => state.settings);
    const { lastRequest } = useSelector((state: ApplicationState) => state.request);
    const { enqueueSnackbar } = useSnackbar();
    const getCurrencyString = useCurrencyString();
    const [loading, setLoading] = React.useState(true);
    const [provider, setProvider] = React.useState<IPaymentProvider>();
    const [error, setError] = React.useState(OperationErrorType.NONE);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [showRetry, setShowRetry] = React.useState(false);
    const [changedPriceOrder, setChangedPriceOrder] = React.useState<
        IOrderCreateResourceV8 & { id: number }
    >();
    const [payInstead, setPayInstead] = React.useState<number>();
    const [changedPriceOrderExtraTip, setChangedPriceOrderExtraTip] = React.useState<
        IOrderCreateResourceV8 & { id: number }
    >();
    const [extraTipValue, setExtraTipValue] = React.useState<number>();

    const timeoutRef = React.useRef<NodeJS.Timeout>(undefined);

    const dispatch = useAppDispatch();
    const { push } = useLocalHistory();
    const location: Location<IOperationHistoryParams> = useLocation();
    const locationState = location.state;
    const { t } = useTranslation();
    const { isGuest, card, user } = useAuth();
    const [scenario] = useRouteScenario();

    const isStandAloneProvider = React.useMemo(
        () => isStandAlonePaymentProvider(settings?.paymentProvider),
        [settings?.paymentProvider]
    );

    React.useEffect(() => {
        // Dojo handles payment and 3DS checks
        if (settings && !provider && !isStandAloneProvider) {
            paymentProviderFactory.create(settings).then(paymentProvider => {
                setProvider(paymentProvider);
            });
        }
        return () => {
            if (provider && provider.reset) {
                provider.reset();
            }
        };
    }, [provider, provider?.reset, settings, isStandAloneProvider]);

    /**
     * Recovers from a THREEDSECURE_AUTH_REQUIRED operation error by running the provider's
     * 3DS challenge and re-submitting the order.
     */
    const retryOrderWithThreeDSecure = React.useCallback(
        async (
            paymentProvider: IPaymentProvider,
            errorData: ThreeDSecureAuthenticationData
        ): Promise<boolean> => {
            const { orderBodyToRecreate, orderBodyToRecreatePat } = locationState ?? {};
            try {
                if (!!orderBodyToRecreate) {
                    const orderBody = await paymentProvider.threeDSecureAuthenticate(
                        errorData,
                        orderBodyToRecreate
                    );
                    if (orderBody === null) {
                        throw new Error('Error while submitting payment');
                    }
                    const operationResponse = await orderApi.createOrder(orderBody);
                    if (isDefined(operationResponse.id)) {
                        push(ROUTES.QUICKPAY.STATUS, { operationId: operationResponse.id.toString() });
                        return true;
                    }
                } else if (!!orderBodyToRecreatePat && isDefined(orderBodyToRecreatePat.id)) {
                    const { id, ...orderToRecreate } = orderBodyToRecreatePat;
                    const orderBody = await paymentProvider.threeDSecureAuthenticate(
                        errorData,
                        orderToRecreate
                    );
                    if (orderBody === null) {
                        throw new Error('Error while submitting payment');
                    }
                    const operationResponse = await orderApi.updateOrder(id, orderBody);
                    if (isDefined(operationResponse.id)) {
                        push(ROUTES.QUICKPAY.STATUS, { operationId: operationResponse.id.toString() });
                        return true;
                    }
                }
            } catch (e) {
                logger.error(e);
                enqueueSnackbar(t('CHECKOUT_ERROR_PLACING_ORDER'), {
                    variant: 'error'
                });
            }
            return false;
        },
        [enqueueSnackbar, locationState, push, t]
    );

    const checkOperationStatus = React.useCallback(async () => {
        setChangedPriceOrder(undefined);
        setPayInstead(undefined);
        if (!operationId || (!isStandAloneProvider && !provider)) {
            return;
        }
        setLoading(true);
        try {
            const operation = await orderApi.getOperation(operationId);
            if (!isDefined(operation?.status)) {
                throw new Error(t('DIALOG_ORDER_STATUS_UNKNOWN'));
            }
            orderApi.nonceHandlingByOperationStatus(operation.status, [
                EOrderNonceKey.CREATE_ORDER,
                EOrderNonceKey.UPDATE_ORDER
            ]);

            switch (operation.status) {
                case OperationStatus.PENDING:
                    // Recursive Poll for order completion
                    timeoutRef.current = setTimeout(() => {
                        checkOperationStatus();
                    }, OPERATION_POLLING_DELAY);
                    break;
                case OperationStatus.ERROR:
                case OperationStatus.TIMEOUT:
                    if (operation.isRetryable) {
                        setShowRetry(true);
                    }
                    // Dojo runs its 3DS challenge inline in DojoPaymentsDialog
                    if (
                        !isStandAloneProvider &&
                        !!provider &&
                        operation.errorCode === OrderErrorCodes.THREEDSECURE_AUTH_REQUIRED &&
                        !!operation.errorData &&
                        (!!locationState?.orderBodyToRecreate || !!locationState?.orderBodyToRecreatePat)
                    ) {
                        setError(OperationErrorType.THREEDSECURE_AUTH_REQUIRED);
                        setLoading(false);
                        if (await retryOrderWithThreeDSecure(provider, operation.errorData)) {
                            return;
                        }
                    }
                    // Overpayment
                    if (
                        operation.errorCode === OrderErrorCodes.OVERPAYMENT &&
                        !!locationState?.orderBodyToRecreatePat &&
                        !!settings
                    ) {
                        const orderBody = locationState.orderBodyToRecreatePat;
                        const [oldPayment] = orderBody.payments.filter(
                            item => item.type !== OrderPaymentType.GIFT_CARD
                        );
                        const filteredPayments = orderBody.payments.filter(
                            item => item.type === OrderPaymentType.GIFT_CARD
                        );
                        if (!!oldPayment) {
                            const filteredAdjustments =
                                orderBody.adjustments?.filter(
                                    item => item.type !== OrderAdjustmentType.TIP
                                ) ?? [];
                            const { errorData } = operation;
                            if (
                                !errorData ||
                                !isDefined(errorData.balanceBefore) ||
                                !isDefined(errorData.paymentError)
                            ) {
                                throw new Error(operation.errorMessage);
                            }
                            const oldTip = orderBody.adjustments?.find(
                                item => item.type === OrderAdjustmentType.TIP
                            );
                            const payInsteadValue = errorData.balanceBefore + (oldTip?.value ?? 0);
                            const extraTip = (oldPayment.amount ?? 0) - payInsteadValue;
                            filteredPayments.push({
                                ...oldPayment,
                                amount: payInsteadValue
                            });
                            filteredAdjustments.push({
                                type: OrderAdjustmentType.TIP,
                                value: (oldTip?.value ?? 0) + extraTip
                            });
                            const orderAlternative: IOrderCreateResourceV8 & { id: number } = {
                                ...orderBody,
                                payments: [...filteredPayments]
                            };
                            const orderExtraTipAlternative: IOrderCreateResourceV8 & { id: number } = {
                                ...orderBody,
                                adjustments: filteredAdjustments
                            };
                            setPayInstead(payInsteadValue);
                            setChangedPriceOrder(orderAlternative);
                            setExtraTipValue(extraTip);
                            setChangedPriceOrderExtraTip(orderExtraTipAlternative);
                            setError(OperationErrorType.GENERIC);
                            setErrorMessage(
                                t('PAT_QUICKPAY_ERROR_OVERPAYMENT', {
                                    amount: getCurrencyString(payInsteadValue)
                                })
                            );
                            return setLoading(false);
                        }
                    }
                    if (!operation.errorCode || !SPECIAL_ERROR_CODES.includes(operation.errorCode)) {
                        throw new Error(operation.errorMessage || 'Operation error or timeout');
                    }
                    if (operation.errorCode === String(OrderErrorCodes.UPDATING_THE_BILL)) {
                        throw new Error(operation.errorMessage);
                    }
                // eslint-disable-next-line no-fallthrough
                case OperationStatus.DONE: {
                    const orderId = operation.resultId ?? null;
                    if (!orderId) {
                        throw new Error('No order id supplied in operation');
                    }
                    if (scenario === OrderScenario.PREORDER && !!timeslots && !!locationId) {
                        dispatch(loadTimeslots({ locationId }));
                    }
                    if (scenario === OrderScenario.TABLE) {
                        dispatch(partialResetBasket());
                    } else {
                        dispatch(resetBasket());
                    }
                    if (isGuest) {
                        addGuestOrder(orderId.toString());
                        if (card) {
                            await userApi.removeCard(card._id);
                            if (user?.id) {
                                await dispatch(getUserCard(user.id)).unwrap();
                            }
                        }
                    }
                    // Redirect
                    push(ROUTES.ORDER.SUMMARY, { orderId: orderId.toString() });
                }
            }
        } catch (orderStatusError: any) {
            if (isDefined(orderStatusError?.response) && orderStatusError.response?.status === 404) {
                setError(OperationErrorType.NOT_FOUND);
                setErrorMessage('Order not found');
            } else {
                setError(OperationErrorType.GENERIC);
                setErrorMessage(orderStatusError);
                if (!isDefined(orderStatusError)) {
                    setErrorMessage(t('DIALOG_ORDER_STATUS_UNKNOWN'));
                    setShowRetry(true);
                }
            }
            setLoading(false);
        }
    }, [
        card,
        dispatch,
        getCurrencyString,
        locationState,
        isStandAloneProvider,
        isGuest,
        locationId,
        operationId,
        provider,
        push,
        retryOrderWithThreeDSecure,
        scenario,
        settings,
        t,
        timeslots,
        user?.id
    ]);
    React.useEffect(() => {
        checkOperationStatus();
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [checkOperationStatus]);

    const handleRetry = React.useCallback(() => {
        if (lastRequest) {
            setError(OperationErrorType.NONE);
            setErrorMessage('');
            lastRequest();
            checkOperationStatus();
        }
    }, [checkOperationStatus, lastRequest]);

    const handleCancel = React.useCallback(() => {
        if (scenario === OrderScenario.ORDER_TO_TABLE || scenario === OrderScenario.PREORDER) {
            orderApi.resetNonceByKey(EOrderNonceKey.CREATE_ORDER);
        } else if (scenario === OrderScenario.TABLE) {
            orderApi.resetNonceByKey(EOrderNonceKey.UPDATE_ORDER);
        }

        if (scenario === OrderScenario.PREORDER) {
            push(ROUTES.BASE);
        } else {
            push(ROUTES.JOURNEY.LANDING);
        }

        dispatch(resetBasket());
    }, [dispatch, push, scenario]);

    return {
        provider,
        handleRetry,
        handleCancel,
        loading,
        error,
        errorMessage,
        showRetry,
        changedPriceOrder,
        payInstead,
        changedPriceOrderExtraTip,
        extraTipValue
    };
}
